/**
 * Weekday + time picker for a ranking's weekly rules — the "Inscrições"
 * activation window and the "Sorteio automático" fallback share it.
 */

export const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda', short: 'Seg' },
  { value: 2, label: 'Terça', short: 'Ter' },
  { value: 3, label: 'Quarta', short: 'Qua' },
  { value: 4, label: 'Quinta', short: 'Qui' },
  { value: 5, label: 'Sexta', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
];

/** Renders an ISO instant as a readable local moment in the ranking's timezone. */
export function formatScheduleMoment(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone,
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleString('pt-BR');
  }
}

/** The device's timezone — for this audience, the zone the ranking is played in. */
export function deviceTimeZone(fallback: string): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
}

export function WeeklyScheduleFields({
  weekday,
  time,
  onWeekdayChange,
  onTimeChange,
}: {
  weekday: number;
  time: string;
  onWeekdayChange: (weekday: number) => void;
  onTimeChange: (time: string) => void;
}) {
  return (
    <>
      <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Dia da semana</p>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {WEEKDAY_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onWeekdayChange(option.value)}
            className={`py-2 rounded-lg text-sm transition-colors ${weekday === option.value
                ? 'bg-turquoise text-deep-blue font-semibold'
                : 'bg-white/10 text-white/60 hover:bg-white/15'
              }`}
          >
            {option.short}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-white/60">Horário</span>
        <input
          type="time"
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-white text-sm focus:outline-none focus:border-turquoise/50"
        />
      </div>
    </>
  );
}

/** A radio-card option, e.g. "Ao encerrar a rodada" vs "Em dia e horário fixos". */
export function ScheduleModeOption({
  selected,
  onSelect,
  title,
  description,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-3 rounded-xl border transition-colors ${selected
          ? 'bg-turquoise/10 border-turquoise/50'
          : 'bg-white/5 border-white/10 hover:bg-white/10'
        }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center ${selected ? 'border-turquoise' : 'border-white/30'
            }`}
        >
          {selected && <span className="w-2 h-2 rounded-full bg-turquoise" />}
        </span>
        <span className="text-sm font-semibold text-white">{title}</span>
      </div>
      <p className="text-xs text-white/50 mt-1 ml-6 leading-snug">{description}</p>
    </button>
  );
}
